import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from '@flash-global66/g-toast';
import { storeToRefs } from 'pinia';
import { openAlert } from '@flash-global66/g-dialog-alert';

// TYPES
import type { CurrencyWalletType } from '@/types/CurrencyWalletType';
import type { AssociateCurrencyPayloadType } from '@/types/services/AssociateCurrencyPayloadType';
import type { CreateWalletDrawerEmit } from '@/modules/wallet/types/createWalletDrawer.type';

// STORES
import { useWalletStore } from '@/stores/wallet';
import { useAccountStore } from '@/stores/account';
import { useRemoteConfigStore } from '@/shared/firebase';

// SHARED
import { walletService } from '@/shared/common/services/wallet.service';
import { getReasonMessage } from '@/shared/common/utils/errorMessages.util';

// HELPERS
import { canCreateStablecoinWallet } from '@/helpers/kyc';

// COMPOSABLES
import useLoader from '@/composables/useLoader';

export function useCreateWalletDrawer(emit: CreateWalletDrawerEmit) {
  const router = useRouter();
  const { showGLoader } = useLoader();
  const walletStore = useWalletStore();
  const accountStore = useAccountStore();
  const remoteConfigStore = useRemoteConfigStore();
  const { isCreateWalletDrawerOpen } = storeToRefs(walletStore);

  const generalFlag = remoteConfigStore.getFeatureFlag('feature_wallet_stablecoins');
  const institutionalConfig = remoteConfigStore.getRemoteConfigValue(
    'feature_wallet_stablecoins_institutional'
  );

  const isStablecoinEnabled = computed<boolean>(() =>
    canCreateStablecoinWallet({
      isInstitutional: Boolean(accountStore.kycStatus?.company?.institutional),
      companyId: String(accountStore.kycStatus?.company?.id ?? ''),
      generalEnabled: generalFlag.value.enabled,
      institutionalEnabled: institutionalConfig.value.enabled,
      institutionalWhitelist: institutionalConfig.value.whitelist,
    })
  );

  function buildPayload(currency: CurrencyWalletType): AssociateCurrencyPayloadType {
    const { kycStatus } = accountStore;
    return {
      businessName: kycStatus.company.name,
      idNumber: kycStatus.company.identificationNumber,
      idType: kycStatus.company.identificationType,
      countryCode: kycStatus.company.addressCountry.country,
      currencyCode: currency.currencyCode,
      legalRepName: kycStatus.b2bUser.name,
      legalRepLastName: kycStatus.b2bUser.lastName,
      legalRepIdNumber: kycStatus.b2bUser.identificationNumber,
      legalRepIdType: kycStatus.b2bUser.identificationType,
      legalRepPhoneNumber: `+${kycStatus.b2bUser.phoneCountryCode}${kycStatus.b2bUser.phoneNumber}`,
      legalRepEmail: kycStatus.b2bUser.email ?? '',
    };
  }

  async function createWallet(currency: CurrencyWalletType) {
    showGLoader(true);
    const payload = buildPayload(currency);
    const result = await walletService.associateCurrency(payload);
    result.fold(
      (failure) => {
        const errorMessage = getReasonMessage({
          failure,
          defaultMessage: 'Ocurrió un error al crear la billetera.',
        });
        toast.error(errorMessage);
      },
      (success) => {
        walletStore.closeCreateWalletDrawer();
        router.push({ name: 'wallet-detail', params: { walletId: success.data.walletId } });
      }
    );
    showGLoader(false);
  }

  function handleCurrencySelected(currency: CurrencyWalletType) {
    if (currency.isStablecoin) {
      if (currency.status === 'ACTIVATION_PENDING') {
        openAlert({
          type: 'info',
          title: `Tu cuenta de ${currency.currencyCode} ya está en proceso`,
          description:
            'En unos minutos estará disponible y te avisaremos cuando esté lista',
          imageSize: 'xl',
          primaryText: 'Entendido',
        });
        return;
      }
      if (!isStablecoinEnabled.value) return;
      walletStore.closeCreateWalletDrawer();
      emit('stablecoin-selected', currency);
      return;
    }
    createWallet(currency);
  }

  function closeDrawer() {
    walletStore.closeCreateWalletDrawer();
  }

  return {
    isOpen: isCreateWalletDrawerOpen,
    handleCurrencySelected,
    closeDrawer,
  };
}
